import { useState } from "react";
import { motion } from "framer-motion";
import { FadeUp } from "./Reveal";

const POOL = [
  { src: "/photos/lakeside.webp", alt: "Bride and groom by the water", caption: "By the Water" },
  { src: "/photos/couple-forest.webp", alt: "Bride and groom in traditional attire sharing an embrace", caption: "The Embrace" },
  { src: "/photos/detail.webp", alt: "Henna-adorned hand with bridal bangles and kalire", caption: "The Details" },
  { src: "/photos/garden-walk.webp", alt: "Bride in white gown turning in a palace garden", caption: "The Garden Walk" },
  { src: "/photos/bride-bridge.webp", alt: "Bride in embroidered red and blush attire on a bridge", caption: "Her Moment" },
  { src: "/photos/red-veil.webp", alt: "Couple sharing a kiss beneath a red veil", caption: "The Red Veil" },
  { src: "/photos/rings-closeup.webp", alt: "Couple holding two wedding rings between them", caption: "Two Rings" },
  { src: "/photos/blossom-tree.webp", alt: "Couple standing together by a blossoming tree", caption: "Under the Blossom" },
  { src: "/photos/party.webp", alt: "Bride and groom dancing at the reception party", caption: "The Party" },
  { src: "/photos/dune-run.webp", alt: "Couple laughing and running through golden dunes", caption: "Chasing the Light" },
  { src: "/photos/beach-picnic.webp", alt: "Couple sharing a picnic on the beach at golden hour", caption: "The Picnic" },
];

const SLOTS = [
  { span: "md:col-span-7", ratio: "aspect-[4/3]" },
  { span: "md:col-span-5", ratio: "aspect-[3/4]" },
  { span: "md:col-span-4", ratio: "aspect-[3/4]" },
  { span: "md:col-span-8", ratio: "aspect-[16/10]" },
  { span: "md:col-span-5", ratio: "aspect-[3/4]" },
  { span: "md:col-span-7", ratio: "aspect-[16/10]" },
  { span: "md:col-span-6", ratio: "aspect-[4/5]" },
  { span: "md:col-span-6", ratio: "aspect-[4/5]" },
];

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export const Gallery = () => {
  const [active, setActive] = useState(null);
  const [photos] = useState(() =>
    shuffle(POOL).slice(0, SLOTS.length).map((p, i) => ({ ...p, ...SLOTS[i] }))
  );

  return (
    <section id="work" className="px-6 md:px-12 py-28 md:py-44 bg-[#0A0A0A]" data-testid="gallery-section">
      <FadeUp className="mb-16 md:mb-24 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B] mb-4">Selected Work</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#F5F4F0] leading-[1.05]">
            Frames that <span className="font-accent italic text-[#C9A26B]">breathe</span>
          </h2>
        </div>
        <p className="text-sm text-[#525252] max-w-xs leading-relaxed">
          A selection from the last few seasons — each one a day we'll never forget either.
        </p>
      </FadeUp>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
        {photos.map((p, i) => (
          <FadeUp key={p.src} delay={(i % 3) * 0.08} className={p.span}>
            <motion.figure
              data-testid={`gallery-item-${i}`}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
              animate={{ opacity: active === null || active === i ? 1 : 0.35 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="group cursor-pointer"
            >
              <div className={`relative overflow-hidden rounded-sm ${p.ratio}`}>
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className="photo-tone w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-[#0A0A0A]/35 group-hover:opacity-0 transition-opacity duration-700" />
              </div>
              <figcaption className="mt-4 flex items-baseline justify-between">
                <span className="text-sm text-[#A3A3A3]">{p.caption}</span>
                <span className="font-accent text-xs tracking-[0.3em] text-[#C9A26B]">{String(i + 1).padStart(2, "0")}</span>
              </figcaption>
            </motion.figure>
          </FadeUp>
        ))}
      </div>
    </section>
  );
};
